import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ApplicationContext } from "../../context/GlobalState";
import { t } from "../../i18n";
import { getLocalizedDisplay } from "../../utils/localizedDisplay";
import HreflangTags from "../../components/HreflangTags/HreflangTags";
import { buildOrganizationSchema } from "../../utils/schemaMarkup";
import { FaPhone, FaCalendarCheck, FaMapMarkerAlt, FaArrowRight } from "react-icons/fa";
import TrustBadges from "../../components/TrustBadges/TrustBadges";
import ReviewsWidget from "../../components/ReviewsWidget/ReviewsWidget";
import SponsorLogosBand from "../../components/SponsorLogosBand/SponsorLogosBand";
import ApexFeatureBand from "../../components/ApexFeatureBand/ApexFeatureBand";
import GsoCarAccidentBand from "../../components/GsoCarAccidentBand/GsoCarAccidentBand";
import GsoCarAccidentInfographic from "../../components/GsoCarAccidentInfographic/GsoCarAccidentInfographic";
import GsoAwardsBand from "../../components/GsoAwardsBand/GsoAwardsBand";
import HomeLatestNews from "../../components/HomeLatestNews/HomeLatestNews";
import FeaturedCategoryCard from "../../components/FeaturedCategoryCard/FeaturedCategoryCard";
import CategoryTileCard from "../../components/CategoryTileCard/CategoryTileCard";
import gaHero from "../../assets/images/heroes/ga-hero-main.webp";
import ncHero from "../../assets/images/heroes/nc-hero-main.webp";
import "./Home.css";

const BRAND_HEROES = {
  gso: gaHero,
  "apex-nc": ncHero,
  "apex-tn": ncHero,
};

export default function Home() {
  const { state } = useContext(ApplicationContext);
  const services = state.taxonomy.dimensions.find((d) => d.id === "service")?.values || [];
  const locations = state.taxonomy.dimensions.find((d) => d.id === "location")?.values || [];
  const categories = state.taxonomy.serviceCategories || [];

  const brandKey = state.brand?.brandKey || "gso";
  const locale = state.locale || 'en';
  const prefix = locale === 'en' ? '' : `/${locale}`;
  const brand = state.brand || state.client;
  const baseDomain = brand?.domain || state.client?.domain || 'gaspineortho.com';
  const orgSchema = buildOrganizationSchema({ brand });
  // Filter locations to current brand only. Honor brand.physicalSiteSlugs when set
  // so the home Locations grid mirrors header/footer (TMM physical sites only).
  const physicalSiteSlugs = brand?.physicalSiteSlugs;
  const brandLocations = (physicalSiteSlugs && physicalSiteSlugs.length
    ? locations.filter((l) => physicalSiteSlugs.includes(l.slug))
        .sort((a, b) => physicalSiteSlugs.indexOf(a.slug) - physicalSiteSlugs.indexOf(b.slug))
    : locations.filter((l) => l.brand === brandKey));
  const displayLocations = brandLocations.length > 0 ? brandLocations : locations;

  return (
    <div className="home-page">
      <Helmet>
        <title>{state.client.name} | Orthopedic, Spine & Injury Care</title>
        <meta name="description" content={`${state.client.name} provides expert orthopedic, spine, and injury care across Georgia, North Carolina, South Carolina, and Tennessee. Free consultations.`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${state.client.name} | Orthopedic, Spine & Injury Care`} />
        <meta property="og:description" content={`${state.client.name} provides expert orthopedic, spine, and injury care. Free consultations.`} />
        <meta property="og:url" content={`https://${baseDomain}/`} />
        <meta property="og:image" content={`https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`} />
        <link rel="canonical" href={`https://${baseDomain}/`} />
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/" />
      {orgSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />}

      {/* Hero */}
      <section
        className="home-hero"
        style={{
          backgroundImage: `linear-gradient(var(--glass-hero-overlay, rgba(26,54,93,0.72)), var(--glass-hero-overlay, rgba(26,54,93,0.72))), url(${BRAND_HEROES[state.brand?.brandKey] || gaHero})`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="container">
          <div className="home-hero-grid">
          <div className="home-hero-content">
            <h1>{t("home.heroTitle", state.locale)}</h1>
            <p>{t("site.tagline", state.locale)}</p>
            <div className="home-hero-actions">
              <Link to={`${prefix}/request-appt`} className="hero-btn-primary"><FaCalendarCheck /> {t("cta.schedule", state.locale)}</Link>
              <a href={`tel:${brand?.phone || state.client.phone}`} className="hero-btn-secondary"><FaPhone /> New Patients: {brand?.phone || state.client.phone}</a>
              {brand?.phoneExisting && (
                <a href={`tel:${brand.phoneExisting}`} className="hero-btn-secondary"><FaPhone /> Existing Patients: {brand.phoneExisting}</a>
              )}
            </div>
            <p className="home-hero-insurance-notice" style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.92)', color: '#7a1f1f', borderLeft: '4px solid #c53030', fontWeight: 600, display: 'inline-block', borderRadius: 4 }}>
              We do not accept or bill government/commercial health insurance plans.
            </p>
          </div>
          <div className="home-hero-cutout" aria-hidden="true">
            <img
              src={brandKey === 'gso'
                ? '/assets/brand/gso/hero/doctors-cutout.webp'
                : '/assets/brand/apex/hero/doctors-cutout.webp'}
              alt=""
              loading="eager"
              decoding="async"
            />
          </div>
          </div>
        </div>
      </section>

      <TrustBadges locale={state.locale} />
      {(brandKey === 'apex-nc' || brandKey === 'apex-tn') && <ApexFeatureBand />}
      {brandKey === 'gso' && <GsoCarAccidentBand />}
      {brandKey === 'gso' && <GsoCarAccidentInfographic />}
      {brandKey === 'gso' && <GsoAwardsBand />}

      <ReviewsWidget widgetSrc={brand?.reviewsWidgetUrl} />

      {/* Services by Category — Hybrid C+E: featured + tiles + A–Z */}
      {(() => {
        const overrides = (brand && brand.serviceCategoryOverrides) || {};
        const primaryLocSlug = displayLocations[0]?.slug || "atlanta";
        const serviceHref = (svc) => `${prefix}/${primaryLocSlug}/${svc.slug}`;
        const categoryHref = (cat) => `${prefix}/services#${cat.id}`;
        const merged = categories.map((c) => ({ ...c, ...(overrides[c.id] || {}) }));
        const enriched = merged.map((c) => {
          const catServices = services.filter((s) => s.category === c.id);
          const marqueeSlugs = (c.marqueeServices && c.marqueeServices.length
            ? c.marqueeServices
            : catServices.slice(0, 3).map((s) => s.slug));
          const marqueeServices = marqueeSlugs
            .map((slug) => catServices.find((s) => s.slug === slug))
            .filter(Boolean)
            .slice(0, 3)
            .map((s) => ({ slug: s.slug, name: getLocalizedDisplay(s, locale), href: serviceHref(s) }));
          return { ...c, serviceCount: catServices.length, marqueeServicesResolved: marqueeServices };
        });
        const sortFn = (a, b) => (a.sortWeight ?? 99) - (b.sortWeight ?? 99);
        const featured = enriched.filter((c) => c.featured && c.serviceCount > 0).sort(sortFn);
        const rest = enriched.filter((c) => !c.featured && c.serviceCount > 0).sort(sortFn);
        const allServicesSorted = [...services].sort((a, b) =>
          getLocalizedDisplay(a, locale).localeCompare(getLocalizedDisplay(b, locale))
        );
        return (
          <section className="home-services section-padding">
            <div className="container">
              <h2>{t("nav.services", state.locale)}</h2>
              <div className="services-featured">
                {featured.map((cat) => (
                  <FeaturedCategoryCard
                    key={cat.id}
                    icon={cat.icon}
                    label={cat.label}
                    teaser={cat.teaser}
                    services={cat.marqueeServicesResolved}
                    ctaHref={categoryHref(cat)}
                    ctaLabel={`View all ${cat.label}`}
                  />
                ))}
              </div>
              <div className="services-tiles">
                {rest.map((cat) => (
                  <CategoryTileCard
                    key={cat.id}
                    icon={cat.icon}
                    label={cat.label}
                    serviceCount={cat.serviceCount}
                    ctaHref={categoryHref(cat)}
                  />
                ))}
              </div>
              <details className="services-az">
                <summary>All Services A–Z ({allServicesSorted.length})</summary>
                <ul className="services-az-list">
                  {allServicesSorted.map((s) => (
                    <li key={s.slug}>
                      <Link to={serviceHref(s)}>{getLocalizedDisplay(s, locale)}</Link>
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          </section>
        );
      })()}

      {brandKey === 'gso' && <SponsorLogosBand />}

      <HomeLatestNews />

      {/* Locations */}
      <section className="home-locations section-padding bg-alt">
        <div className="container">
          <h2>{t("nav.locations", state.locale)}</h2>
          <div className="locations-grid">
            {displayLocations.map((loc) => (
              <Link key={loc.slug} to={`${prefix}/${loc.slug}`} className="location-card">
                <FaMapMarkerAlt className="location-icon" />
                <h3>{loc.city}, {loc.state}</h3>
                <p>{loc.address}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
